import React, { useMemo, useState } from "react";
import { SettingsFormContent } from "@/components/pages/Settings";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import Space from "@/components/atoms/Space";
import { Address } from "@/types";
import { useGetCompanyAddresses } from "@/services/Company";
import { ColumnDef } from "@tanstack/react-table";
import Group from "@/components/atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteAddress, postNewAddress, setDefaultAddress, updateAddress } from "@/services/Settings";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import AddOrEditAddressModal from "../AddOrEditAddressModal";
import { AddressFormInput, UpdateAddressInput } from "@/components/pages/Settings/validation/addressValidationSchema";
import Badge from "@/components/atoms/Badge";

interface CompanyAddressTableProps {
  isAdmin: boolean;
  currentCompanyId: number | undefined;
}

const CompanyAddressTable = (props: CompanyAddressTableProps) => {
  const [isNewAddress, setIsNewAddress] = useState<boolean>(false);
  const [isAddEditAddressOpen, setIsAddEditAddressOpen] = useState<boolean>(false);
  const [address, setAddress] = useState<Address | undefined>(undefined);

  const queryClient = useQueryClient();

  const { isAdmin, currentCompanyId } = props;
  const { data: companyAddress } = useGetCompanyAddresses(currentCompanyId as number, {
    enabled: !!currentCompanyId,
  });
  const { t } = useTranslation();

  const { mutateAsync: deleteAddressMutation } = useMutation({
    mutationFn: async (selectedAddress: Address) => {
      if (!selectedAddress) {
        console.error("Error: Address to delete is missing.");
        return;
      }
      if (selectedAddress.addressType === AddressTypeEnum.SHIPPING && selectedAddress.isDefault) {
        notifications.show(
          ErrorNotificationObj(
            t("notifications:generic.deleteError"),
            t("settings:deleteDefaultShippingAddressWarning")
          )
        );
        return;
      }
      await deleteAddress(currentCompanyId as number, selectedAddress?.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", currentCompanyId],
      });
      setIsAddEditAddressOpen(false);
    },
    onError: (error) => {
      console.error("Error deleting address", error);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.deleteError"), t("notifications:settings.deleteAddressError"))
      );
    },
  });

  const { mutateAsync: createAddressMutation } = useMutation({
    mutationFn: async (data: AddressFormInput) => {
      await postNewAddress(currentCompanyId as number, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", currentCompanyId],
      });
      setIsAddEditAddressOpen(false);
    },
    onError: (error) => {
      console.error("Error creating address", error);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.createError"), t("notifications:settings.createAddressError"))
      );
    },
  });

  const { mutateAsync: setDefaultAddressMutation } = useMutation({
    mutationFn: async (selectedAddress: Address) => {
      await setDefaultAddress(currentCompanyId as number, selectedAddress.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", currentCompanyId],
      });
      setIsAddEditAddressOpen(false);
    },
    onError: (error) => {
      console.error("Error creating address", error);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.createError"), t("notifications:settings:createAddressError"))
      );
    },
  });

  const { mutateAsync: updateAddressMutation } = useMutation({
    mutationFn: async (data: UpdateAddressInput) => {
      console.log("Updating address with data:", data);
      await updateAddress(currentCompanyId as number, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyAddresses", currentCompanyId],
      });
      setIsAddEditAddressOpen(false);
    },
    onError: (error) => {
      console.error("Error updating address", error);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.updateError"), t("notifications:settings.updateAddressError"))
      );
    },
  });

  const columns: ColumnDef<Address>[] = useMemo(
    () => [
      {
        header: t("common:address"),
        accessorKey: "address",
      },
      {
        header: t("common:address2"),
        accessorKey: "address2",
      },
      {
        header: t("common:address3"),
        accessorKey: "address3",
      },
      {
        header: t("common:postCode"),
        accessorKey: "postCode",
      },
      {
        header: t("common:place"),
        accessorKey: "place",
      },
      {
        header: t("common:country"),
        accessorKey: "country.name",
      },
      {
        header: t("common:type"),
        accessorKey: "addressType",
        cell: (info) => {
          const rawAddressType = info.getValue() as typeof AddressTypeEnum;
          const row = info.row.original;
          return (
            <Group gap="xs">
              <span>{t(`enumerables:addressTypes.${rawAddressType}`)}</span>
              {row.isDefault ? (
                <Badge color="primary" noTextTransform>
                  {t("settings:default")}
                </Badge>
              ) : null}
            </Group>
          );
        },
      },
    ],
    [t]
  );

  const editActions = (address: Address) => {
    const actions = [
      {
        label: t("settings:edit"),
        action: () => {
          setIsNewAddress(false);
          setIsAddEditAddressOpen(true);
          setAddress(address);
        },
      },
    ];

    if (address.addressType === AddressTypeEnum.SHIPPING && !address.isDefault) {
      actions.push({
        label: t("settings:setAsDefault"),
        action: async () => {
          await setDefaultAddressMutation(address);
        },
      });
    }

    actions.push({
      label: t("settings:delete"),
      action: async () => {
        await deleteAddressMutation(address);
      },
    });

    return actions;
  };

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase">
            {t("common:address")}
          </Text>
          <CTA
            disabled={!isAdmin}
            id="add-konto"
            intent="secondary"
            onClick={() => {
              setIsNewAddress(true);
              setIsAddEditAddressOpen(true);
              setAddress(undefined);
            }}
          >
            {t("settings:addAddress")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable data={companyAddress || []} columns={columns} editActions={editActions} isAdmin={isAdmin} />
      </SettingsFormContent>

      {isNewAddress ? (
        <AddOrEditAddressModal
          mode="create"
          isAddEditAddressOpen={isAddEditAddressOpen}
          setIsAddEditAddressOpen={setIsAddEditAddressOpen}
          companyId={currentCompanyId}
          callback={createAddressMutation}
        />
      ) : (
        address && (
          <AddOrEditAddressModal
            mode="update"
            isAddEditAddressOpen={isAddEditAddressOpen}
            setIsAddEditAddressOpen={setIsAddEditAddressOpen}
            companyId={currentCompanyId}
            selectedAddress={address}
            callback={updateAddressMutation}
          />
        )
      )}
    </React.Fragment>
  );
};

export default CompanyAddressTable;
